import { z } from 'zod';
import { ERROR_MESSAGES } from '@/lib/utils/constants';
import { VariableType, PricingModelType } from '@/types';

/**
 * Schemas de validación para modelos de pricing
 */

export const pricingModelSchema = z.object({
  name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
  code: z
    .string()
    .min(1, 'El código es obligatorio')
    .max(50, ERROR_MESSAGES.MAX_LENGTH(50))
    .toUpperCase(),
  description: z.string().max(500, ERROR_MESSAGES.MAX_LENGTH(500)).optional(),
  type: z.nativeEnum(PricingModelType).optional().default(PricingModelType.STANDARD),
  targetMarginPercentage: z.number().min(0).max(100).optional().default(0),
  floorMarginPercentage: z.number().min(0).max(100).optional().default(0),
});

export const enumOptionSchema = z.object({
  label: z.string().min(1, 'Etiqueta requerida'),
  value: z.number(),
});

export const tierSchema = z.object({
  minQuantity: z.number().nonnegative('Cantidad mínima no puede ser negativa'),
  maxQuantity: z.number().positive().optional().nullable(),
  unitPrice: z.number().optional().nullable(),
  discount: z.number().min(0).max(100).optional().nullable(),
  order: z.number().int().nonnegative().optional(),
});

export const variableSchema = z
  .object({
    name: z.string().min(2, ERROR_MESSAGES.MIN_LENGTH(2)).max(100, ERROR_MESSAGES.MAX_LENGTH(100)),
    code: z
      .string()
      .min(2, ERROR_MESSAGES.MIN_LENGTH(2))
      .max(50, ERROR_MESSAGES.MAX_LENGTH(50))
      .toUpperCase()
      .regex(
        /^[A-Z_][A-Z0-9_]*$/,
        'Solo mayúsculas, números y guión bajo (_). Debe empezar con letra o guión bajo. Ejemplo: COSTO_BASE, IMPUESTO_1'
      ),
    variableType: z.nativeEnum(VariableType),
    defaultValue: z.any().optional(),
    formula: z.string().optional(),
    description: z.string().max(500, ERROR_MESSAGES.MAX_LENGTH(500)).optional(),
    isGlobal: z.boolean().optional().default(false),
    priceRole: z
      .enum([
        'COST_INPUT',
        'COST_BASE_MARGIN',
        'UNIT_COST',
        'QUANTITY',
        'UNIT_PRICE',
        'TOTAL_PRICE',
        'MARGIN',
        'MARGIN_PCT',
        'TAX',
      ])
      .optional()
      .nullable(),
    order: z.number().min(0, 'El orden no puede ser negativo'),
    options: z.array(enumOptionSchema).optional().nullable(),
    tiers: z.array(tierSchema).optional().nullable(),
    lookupTableId: z.string().uuid().optional().nullable(),
  })
  .refine(
    (data) => {
      // Si es calculada, debe tener fórmula
      if (data.variableType === VariableType.CALCULATED) {
        return !!data.formula && data.formula.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Las variables calculadas deben tener una fórmula',
      path: ['formula'],
    }
  )
  .refine(
    (data) => {
      // Validar sintaxis básica de la fórmula
      if (data.variableType === VariableType.CALCULATED && data.formula) {
        // Verificar paréntesis balanceados
        let parenthesisCount = 0;
        for (const char of data.formula) {
          if (char === '(') parenthesisCount++;
          if (char === ')') parenthesisCount--;
          if (parenthesisCount < 0) return false;
        }
        return parenthesisCount === 0;
      }
      return true;
    },
    {
      message: 'La fórmula tiene paréntesis desbalanceados',
      path: ['formula'],
    }
  )
  .refine(
    (data) => {
      // ENUM debe tener al menos 2 opciones
      if (data.variableType === VariableType.ENUM) {
        return data.options && data.options.length >= 2;
      }
      return true;
    },
    {
      message: 'Las variables ENUM requieren al menos 2 opciones',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // TIERED debe tener al menos 1 tier
      if (data.variableType === VariableType.TIERED) {
        return data.tiers && data.tiers.length >= 1;
      }
      return true;
    },
    {
      message: 'Las variables TIERED requieren al menos 1 nivel de precio',
      path: ['tiers'],
    }
  );

export const lookupTableSchema = z.object({
  name: z.string().min(1, 'Nombre requerido'),
  code: z.string().min(1, 'Código requerido').toUpperCase(),
  rowDimension: z.string().min(1, 'Dimensión de fila requerida'),
  colDimension: z.string().min(1, 'Dimensión de columna requerida'),
  data: z.object({
    rows: z.array(z.string()).min(1),
    cols: z.array(z.string()).min(1),
    values: z.array(z.array(z.number())),
  }),
  isActive: z.boolean().optional().default(true),
});

export type PricingModelFormData = z.infer<typeof pricingModelSchema>;
export type VariableFormData = z.infer<typeof variableSchema>;
export type LookupTableFormData = z.infer<typeof lookupTableSchema>;
